import React, { useEffect, useState, useCallback, useRef } from "react";
import { Modal, Spin, Steps, Typography } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import {
  StepsContainer,
  IframeContainer,
  SpinnerContainer,
  TimeoutMessage,
} from "./three-ds-modal.styled";

const { Text } = Typography;

const TIMEOUT_SECONDS = 120;

interface ThreeDSModalProps {
  open: boolean;
  iframeContent: string | null;
  isCompletingPayment: boolean;
}

function ThreeDSModal({ open, iframeContent, isCompletingPayment }: ThreeDSModalProps) {
  const [callbackDetected, setCallbackDetected] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const iframeLoadCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      setCallbackDetected(false);
      setTimedOut(false);
      iframeLoadCount.current = 0;
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open]);

  useEffect(() => {
    if (open && iframeContent) {
      timerRef.current = setTimeout(() => {
        setTimedOut(true);
      }, TIMEOUT_SECONDS * 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, iframeContent]);

  const handleIframeLoad = useCallback(() => {
    iframeLoadCount.current += 1;
    if (iframeLoadCount.current > 1) {
      setCallbackDetected(true);
    }
  }, []);

  const currentStep = isCompletingPayment || callbackDetected ? 2 : 1;

  const spinText =
    callbackDetected || isCompletingPayment
      ? "Completando pago, por favor espere..."
      : "Verificando con su banco...";

  return (
    <Modal
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SafetyCertificateOutlined style={{ color: "#52c41a", fontSize: 20 }} />
          Verificación de seguridad (3D Secure)
        </span>
      }
      open={open}
      footer={null}
      closable={false}
      width={640}
      centered
      maskClosable={false}
    >
      <StepsContainer>
        <Steps
          current={currentStep}
          items={[
            { title: "Datos de tarjeta", status: "finish" },
            { title: "Verificación bancaria", status: currentStep === 1 ? "process" : "finish" },
            { title: "Procesando pago", status: currentStep === 2 ? "process" : "wait" },
          ]}
        />
      </StepsContainer>

      {iframeContent && (
        <IframeContainer>
          <iframe
            srcDoc={iframeContent}
            style={{ width: "100%", height: 480, border: "none", display: "block" }}
            title="3DS Challenge"
            onLoad={handleIframeLoad}
          />
        </IframeContainer>
      )}

      {timedOut ? (
        <TimeoutMessage>
          <Text type="warning" strong style={{ fontSize: 14 }}>
            La verificación está tomando más tiempo del esperado. Por favor, comuníquese con el
            departamento financiero o intente nuevamente.
          </Text>
        </TimeoutMessage>
      ) : (
        <SpinnerContainer>
          <Spin size="large" />
          <Text type="secondary" style={{ fontSize: 14 }}>
            {spinText}
          </Text>
        </SpinnerContainer>
      )}
    </Modal>
  );
}

export default ThreeDSModal;
